/**
 * RouteRenderer - Renders routes from the centralized route configuration
 *
 * This component uses the route config from routes.ts to dynamically render
 * routes with appropriate layouts based on the current platform (mobile/desktop).
 */

import { Box, SxProps, Theme } from "@mui/material";
import { SuspenseFallback } from "common/components/LoadingScreen";
import { useUserType } from "common/utils";
import { MutableRefObject, Suspense } from "react";
import { MapRef } from "react-map-gl/mapbox";
import { Route, Routes } from "react-router-dom";
import { PrivateRoute } from "./index";
import { LayoutType, RouteConfig, routes, UserType } from "./routes";

interface RouteRendererProps {
  mapRef: MutableRefObject<MapRef | null>;
  platform: "mobile" | "desktop";
  layoutFilter?: LayoutType | LayoutType[];
  excludeLayouts?: LayoutType[];
  excludeSpecialHandling?: boolean;
  includeNoneLayout?: boolean;
}

// Mobile full-screen panel styles
const mobileFullScreenSx: SxProps<Theme> = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  height: "100vh",
  overflow: "auto",
  zIndex: 1200,
  backgroundColor: "white",
};

/**
 * Get the appropriate layout wrapper styles for a route
 *
 * NOTE: Desktop routes do NOT get panel wrapper styles here.
 * DesktopLayout is responsible for the panel container with overflow:hidden and borderRadius.
 * This keeps the clipping in a single place and ensures rounded corners work properly.
 */
function getLayoutStyles(
  route: RouteConfig,
  platform: "mobile" | "desktop"
): SxProps<Theme> | undefined {
  const layoutType =
    platform === "mobile" ? route.layout.mobile : route.layout.desktop;

  // Desktop routes: NO wrapper styles - DesktopLayout handles the panel
  // This is intentional to avoid duplicate panels and ensure proper rounded corner clipping
  if (platform === "desktop") {
    return undefined;
  }

  // Mobile full-screen styles
  if (platform === "mobile" && layoutType === "full-screen") {
    return mobileFullScreenSx;
  }

  return undefined;
}

/**
 * Render a single route with its component and layout
 */
function RouteElement({
  route,
  mapRef,
  platform,
}: {
  route: RouteConfig;
  mapRef: MutableRefObject<MapRef | null>;
  platform: "mobile" | "desktop";
}) {
  const isMobile = platform === "mobile";
  const Component =
    isMobile && route.mobileComponent ? route.mobileComponent : route.component;
  const styles = getLayoutStyles(route, platform);
  const props = route.needsMapRef ? { mapRef } : {};

  const element = (
    <Suspense fallback={<SuspenseFallback />}>
      {styles ? (
        <Box sx={styles}>
          <Component {...props} />
        </Box>
      ) : (
        <Component {...props} />
      )}
    </Suspense>
  );

  if (route.requiresAuth) {
    return <PrivateRoute>{element}</PrivateRoute>;
  }

  return element;
}

/**
 * Check if a route is accessible by a given user type
 */
function isRouteAccessibleByUserType(
  routeUserType: UserType,
  currentUserType: "farmer" | "corporate"
): boolean {
  if (routeUserType === "all") return true;
  return routeUserType === currentUserType;
}

/**
 * RouteRenderer - Main component for rendering routes
 *
 * @param mapRef - Reference to the map component
 * @param platform - Current platform (mobile/desktop)
 * @param layoutFilter - Only render routes with these layout types
 * @param excludeLayouts - Exclude routes with these layout types
 * @param excludeSpecialHandling - If true, exclude routes with special desktop handling
 * @param includeNoneLayout - If true, include routes with "none" layout (render without wrapper)
 */
export default function RouteRenderer({
  mapRef,
  platform,
  layoutFilter,
  excludeLayouts,
  excludeSpecialHandling = false,
  includeNoneLayout = false,
}: RouteRendererProps) {
  const userType = useUserType();

  // Filter routes based on layout and user type
  const filteredRoutes = routes.filter((route) => {
    const layoutType =
      platform === "mobile" ? route.layout.mobile : route.layout.desktop;

    // Filter by user type - only show routes accessible by the current user
    if (!isRouteAccessibleByUserType(route.userType, userType)) {
      return false;
    }

    // Handle "none" layouts - exclude by default unless explicitly included
    // "none" layouts bypass the layoutFilter check since they're handled separately
    if (layoutType === "none") {
      return includeNoneLayout;
    }

    // Exclude routes with special desktop handling if requested
    if (
      excludeSpecialHandling &&
      platform === "desktop" &&
      route.hasSpecialDesktopHandling
    ) {
      return false;
    }

    // Apply layout filter if provided (doesn't apply to "none" layouts which are handled above)
    if (layoutFilter) {
      const filters = Array.isArray(layoutFilter)
        ? layoutFilter
        : [layoutFilter];
      if (!filters.includes(layoutType)) return false;
    }

    // Apply exclusion filter if provided
    if (excludeLayouts && excludeLayouts.includes(layoutType)) {
      return false;
    }

    return true;
  });

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>
        {filteredRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <RouteElement route={route} mapRef={mapRef} platform={platform} />
            }
          />
        ))}
        {/* Catch-all for unmatched paths - prevents "no routes matched" warnings */}
        <Route path="*" element={null} />
      </Routes>
    </Suspense>
  );
}

/**
 * DesktopRouteRenderer - Convenience component for desktop routes
 * Excludes routes with special desktop handling (they have custom components in App.tsx)
 * Includes "none" layout routes (dialogs that render themselves as overlays)
 */
export function DesktopRouteRenderer({
  mapRef,
}: {
  mapRef: MutableRefObject<MapRef | null>;
}) {
  return (
    <RouteRenderer
      mapRef={mapRef}
      platform="desktop"
      layoutFilter={["panel", "full-screen"]}
      excludeSpecialHandling={true}
      includeNoneLayout={true}
    />
  );
}

/**
 * MobileRouteRenderer - Convenience component for mobile non-farm-tab routes
 */
export function MobileRouteRenderer({
  mapRef,
}: {
  mapRef: MutableRefObject<MapRef | null>;
}) {
  return (
    <RouteRenderer
      mapRef={mapRef}
      platform="mobile"
      excludeLayouts={["bottom-sheet"]}
    />
  );
}
